import {
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import ButtonLoader from 'components/ButtonLoader'
import { STYLES_MARGIN } from 'modules/styles/constants/index.cjs'
import React from 'react'
import useDispatch from 'modules/component/utils/hooks/useDispatch'
import StripeCardForm from 'containers/Payment/Stripe/Form/CardForm/index.js'
import { logUtil } from 'packages/src'
import StripePaymentButton from 'containers/Payment/Stripe/Form/PaymentButton/index.js'
import { createTokenAndPlaceOrderStripePaymentAction } from 'containers/Payment/Stripe/actions/create-token-and-place-order/index.js'
import { View } from 'components/View.js'
import { STRIPE_FORM_ID } from 'modules/stripe/constants/index.js'
import { updateIsFormCompleteStripeAction } from 'modules/stripe/redux/actions/update-is-form-complete/index.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import { isFormCompleteStripeSelect } from 'modules/stripe/redux/selectors/is-form-complete/index.js'
import { initPaymentRequestStripeAction } from 'modules/stripe/redux/actions/init-payment-request/index.js'
import { isLoadingStripeSelect } from 'modules/stripe/redux/selectors/is-loading/index.js'
import { shouldCreateTokenStripeSelect } from 'modules/stripe/redux/selectors/should-create-token/index.js'

/**
 * @param isDisabled
 * @param FloatingButton
 * @param props
 * @returns {Element}
 * @constructor
 */
export const PaymentStripeFormCheckout = React.memo(function PaymentStripeFormCheckout ({
  isDisabled,
  FloatingButton,
  ...props
}) {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [canMakePayment, setCanMakePayment] = React.useState(
    props.shouldNotTryPaymentRequest ? false : null
  )
  const isFormComplete = useSelector(isFormCompleteStripeSelect)
  const [paymentRequest, setPaymentRequest] = React.useState()
  const shouldCreateToken = useSelector(shouldCreateTokenStripeSelect)

  React.useEffect(() => {
    dispatch(updateIsFormCompleteStripeAction())
  }, [])

  React.useEffect(() => {
    if (stripe && canMakePayment == null) {
      initPaymentRequest()
    }
  }, [stripe])

  const initPaymentRequest = async () => {
    await dispatch(
      initPaymentRequestStripeAction({
        stripe,
        setCanMakePayment,
        setPaymentRequest,
        productLabel: props.productLabel,
        amount: props.amount,
        onSubmit: props.onSubmit
      })
    )
  }

  const createTokenAndPlaceOrder = React.useCallback(async () => {
    await dispatch(
      createTokenAndPlaceOrderStripePaymentAction({
        elements,
        stripe,
        onSubmit: props.onSubmit
      })
    )
  }, [elements])

  React.useEffect(() => {
    if (shouldCreateToken) {
      createTokenAndPlaceOrder()
    }
  }, [
    shouldCreateToken,
    elements
  ])

  const handleSubmit = React.useCallback(() => {
    createTokenAndPlaceOrder()
  }, [elements])

  const handleChange = React.useCallback((changeObj) => {
    dispatch(updateIsFormCompleteStripeAction(changeObj.complete))
  }, [])

  const isLoading = useSelector(isLoadingStripeSelect)

  const debugProps = {
    paymentRequest,
    canMakePayment
  }
  logUtil('PaymentStripeFormCheckout').debug('debugProps=', debugProps)

  const disabled = (
    isDisabled ||
    !isFormComplete ||
    isLoading
  )

  const propsToPassToButton = {
    onClick: handleSubmit,
    isDisabled: disabled,
    isLoading
  }

  return (
    <View id={STRIPE_FORM_ID}>
      {!!canMakePayment && <StripePaymentButton paymentRequest={paymentRequest} />}

      {
        canMakePayment === false && (
          <>
            <StripeCardForm
              onChange={handleChange}
              onSubmit={handleSubmit}
            />

            {
              FloatingButton
                ? <FloatingButton {...propsToPassToButton} />
                : (
                  <ButtonLoader
                    {...propsToPassToButton}
                    style={{
                      margin: `${STYLES_MARGIN.DEFAULT} 0 0`,
                      width: '100%'
                    }}
                  >
                    {props.buttonLabel}
                  </ButtonLoader>
                  )
            }
          </>
        )
      }
    </View>
  )
})
